import React, { useEffect, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";
import * as LocalStore from "../services/LocalStore";
import { useToast } from "./ToastProvider";
import { useNavigate } from "react-router-dom";

type Contract = {
  name?: string;
  startDate?: string;
  endDate?: string;
  totalHours?: number;
};

export default function Contracts({
  onViewProjection,
}: {
  onViewProjection?: (name: string) => void;
}) {
  const [items, setItems] = useState<Contract[]>([]);
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [hours, setHours] = useState("");
  const toast = useToast();
  // editName removed - LocalStore.saveContract will upsert based on name

  useEffect(() => {
    load();
  }, []);

  async function load() {
    const data = await LocalStore.getContracts();
    setItems(data ?? []);
  }

  async function onSave(e?: React.FormEvent) {
    if (e) e.preventDefault();
    const payload = {
      name: name.trim(),
      startDate: start,
      endDate: end,
      totalHours: Number(hours),
    };
    const saved = await LocalStore.saveContract(payload as any);
    if (!saved) toast.show("Save failed", "error");
    else {
      setName("");
      setStart("");
      setEnd("");
      setHours("");
      await load();
    }
  }

  function onEdit(c: Contract) {
    if (!c.name) {
      toast.show("Cannot edit unnamed contract", "error");
      return;
    }
    setName(c.name);
    setStart(c.startDate?.slice(0, 10) ?? "");
    setEnd(c.endDate?.slice(0, 10) ?? "");
    setHours(String(c.totalHours ?? ""));
  }

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Contract | null>(null);

  function requestDelete(c: Contract) {
    if (!c.name) {
      toast.show("Cannot delete unnamed contract", "error");
      return;
    }
    setToDelete(c);
    setConfirmOpen(true);
  }

  async function doDelete() {
    if (!toDelete || !toDelete.name) return;
    const ok = await LocalStore.deleteContract(toDelete.name);
    setConfirmOpen(false);
    setToDelete(null);
    if (ok) await load();
    else toast.show("Delete failed", "error");
  }

  return (
    <div>
      <h1>Create Contract</h1>
      <form id="contractForm" onSubmit={onSave}>
        <label>Name</label>
        <input
          id="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <label>Start Date</label>
        <input
          id="start"
          type="date"
          required
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
        <label>End Date</label>
        <input
          id="end"
          type="date"
          required
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        />
        <label>Total Hours</label>
        <input
          id="hours"
          type="number"
          step="0.01"
          min={0}
          required
          value={hours}
          onChange={(e) => setHours(e.target.value)}
        />
        <button type="submit">Save</button>
      </form>

      <h2>All Contracts</h2>
      <ul id="list">
        {items.map((c) => {
          const n = c.name ?? "(unnamed)";
          return (
            <li key={n}>
              {`${n} | ${c.startDate?.slice(0, 10)} → ${c.endDate?.slice(
                0,
                10
              )} | ${c.totalHours}h`}
              <button style={{ marginLeft: 8 }} onClick={() => onEdit(c)}>
                Edit
              </button>
              <button
                style={{ marginLeft: 8 }}
                onClick={() => {
                  if (onViewProjection && c.name) {
                    onViewProjection(c.name);
                  } else if (c.name) {
                    navigate(`/projections?name=${encodeURIComponent(c.name)}`);
                  }
                }}
              >
                View Projection
              </button>
              <button
                style={{ marginLeft: 8 }}
                onClick={() => requestDelete(c)}
              >
                Delete
              </button>
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={confirmOpen}
        title={`Delete contract "${toDelete?.name}"?`}
        onConfirm={doDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
